import * as React from 'react';
import type { ComponentProps, ReactElement } from 'react';

import { Badge } from '~shared/components/Badge';

import { FileDropFile } from './FileDropFile/FileDropFile';
import { FileDropInput } from './FileDropInput';
import type { FileDropCallbacks, FileTask } from './hooks/useFileDropConfig';

interface FileDropContainerProps extends FileDropCallbacks {
  files: Array<FileTask>;
  isDisabled?: boolean;
  maxFileCount: number;
  currentFilesCount: number;
  acceptedFileTypes: string[];
  label: ReactElement<ComponentProps<typeof FileDropContainerLabel>>;
  fileDropButtonText: ReactElement<
    ComponentProps<typeof FileDropContainerButtonText>
  >;
  fileDropButtonHelperText: ReactElement<
    ComponentProps<typeof FileDropContainerButtonHelperText>
  >;
  helperText?: ReactElement<ComponentProps<typeof FileDropContainerHelperText>>;
}

export function FileDropContainer({
  maxFileCount,
  isDisabled = false,
  label,
  files,
  helperText,
  currentFilesCount,
  addFileTasks,
  removeFileTasks,
  retryFileTasks,
  acceptedFileTypes,
  fileDropButtonText,
  fileDropButtonHelperText,
}: FileDropContainerProps) {
  return (
    <div className="flex flex-col gap-4 group">
      {label}
      <FileDropInput
        currentFilesCount={currentFilesCount}
        maxFileCount={maxFileCount}
        onFilesSelected={addFileTasks}
        acceptedFileTypes={acceptedFileTypes}
        isDisabled={isDisabled}
      >
        <span className="font-sans text-base font-600 leading-8">
          {fileDropButtonText}
        </span>
        <span className="text-xs text-blue-600 leading-normal">
          {fileDropButtonHelperText}
        </span>
      </FileDropInput>
      {helperText}
      <div className="flex flex-col gap-2 text-blue-900">
        {files.length > 0 &&
          files.map((task) => {
            switch (task.status) {
              case 'pending': {
                const { id, file } = task;
                return (
                  <FileDropFile.Progress
                    key={id}
                    loaded={0}
                    total={0}
                    lengthComputable={false}
                    fileName={file.name}
                    handleRemove={() => {
                      removeFileTasks([id]);
                    }}
                  />
                );
              }
              case 'success': {
                const { id, file } = task;
                return (
                  <FileDropFile.Success
                    uploadType={maxFileCount > 1 ? 'multiple' : 'single'}
                    key={id}
                    fileName={file.name}
                    handleRemove={() => {
                      removeFileTasks([id]);
                    }}
                  />
                );
              }
              case 'progress': {
                const { id, file, loaded, total, lengthComputable } = task;
                return (
                  <FileDropFile.Progress
                    key={id}
                    fileName={file.name}
                    handleRemove={() => {
                      removeFileTasks([id]);
                    }}
                    loaded={loaded}
                    total={total}
                    lengthComputable={lengthComputable}
                  />
                );
              }
              case 'error': {
                const { id, file, error } = task;
                return (
                  <FileDropFile.Error
                    multiple={maxFileCount > 1}
                    error={error}
                    key={id}
                    fileName={file.name}
                    handleRemove={() => {
                      removeFileTasks([id]);
                    }}
                    handleRetry={() => retryFileTasks([id])}
                  />
                );
              }
              default: {
                const never: never = task;
                return <>{never} is not a valid file status</>;
              }
            }
          })}
      </div>
    </div>
  );
}

interface FileDropContainerButtonTextProps {
  children: React.ReactNode;
}

function FileDropContainerButtonText({
  children,
}: FileDropContainerButtonTextProps) {
  return <div>{children}</div>;
}

interface FileDropContainerButtonHelperTextProps {
  children: React.ReactNode;
}

function FileDropContainerButtonHelperText({
  children,
}: FileDropContainerButtonHelperTextProps) {
  return <div>{children}</div>;
}

interface FileDropContainerHelperTextProps {
  children: React.ReactNode;
}

function FileDropContainerHelperText({
  children,
}: FileDropContainerHelperTextProps) {
  return <div className="text-xs text-blue-600 leading-normal">{children}</div>;
}

interface FileDropContainerLabelProps {
  label: string;
  badge?: React.ReactElement<React.ComponentProps<typeof Badge>>;
}

function FileDropContainerLabel({ label, badge }: FileDropContainerLabelProps) {
  return (
    <div className="flex flex-row gap-2">
      <label htmlFor="document" className="font-sans text-base text-blue-900">
        {label}
      </label>
      {badge ? badge : null}
    </div>
  );
}

FileDropContainer.ButtonText = FileDropContainerButtonText;
FileDropContainer.ButtonHelperText = FileDropContainerButtonHelperText;
FileDropContainer.HelperText = FileDropContainerHelperText;
FileDropContainer.Label = FileDropContainerLabel;
